import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from './api.service';
import {
  CompanyDocumentTemplate,
  TemplateSearchRequest,
  TemplateApiResponse,
  TemplateChecklistMapEntry
} from '../types/Contract';

/**
 * Service for Company Document Template Master Table APIs
 * Base URL: /companyTemplate
 */
@Injectable({
  providedIn: 'root'
})
export class CompanyTemplateService {

  private readonly BASE_PATH = 'companyTemplate';

  constructor(private apiService: ApiService) {}

  /**
   * Create a new company document template
   * POST /companyTemplate/create
   */
  createTemplate(template: Partial<CompanyDocumentTemplate>): Observable<TemplateApiResponse<{ template: CompanyDocumentTemplate }>> {
    return this.apiService.create(`${this.BASE_PATH}/create`, template);
  }

  /**
   * Update an existing company document template
   * PUT /companyTemplate/update
   */
  updateTemplate(template: CompanyDocumentTemplate): Observable<TemplateApiResponse<{ template: CompanyDocumentTemplate }>> {
    return this.apiService.updatePut(`${this.BASE_PATH}/update`, template);
  }

  /**
   * Get a company document template by ID
   * GET /companyTemplate/{comDocumentId}
   */
  getTemplateById(comDocumentId: string): Observable<TemplateApiResponse<{ template: CompanyDocumentTemplate }>> {
    return this.apiService.query(`${this.BASE_PATH}/${comDocumentId}`);
  }

  /**
   * Get all templates for a business (includes business-specific + system-defined)
   * GET /companyTemplate/business/{businessId}
   */
  getTemplatesByBusiness(businessId: string): Observable<TemplateApiResponse<{ templates: CompanyDocumentTemplate[] }>> {
    return this.apiService.query(`${this.BASE_PATH}/business/${businessId}`);
  }

  /**
   * Search company document templates with filters and pagination
   * POST /companyTemplate/findCompanyTemplate
   */
  searchTemplates(searchRequest: TemplateSearchRequest): Observable<TemplateApiResponse<{ templates: CompanyDocumentTemplate[]; templateChecklistMap?: { [comDocumentId: string]: TemplateChecklistMapEntry } }>> {
    return this.apiService.create(`${this.BASE_PATH}/findCompanyTemplate`, searchRequest);
  }

  /**
   * Delete a company document template (soft delete - sets to ARCHIVED)
   * Note: System-defined templates cannot be deleted
   * DELETE /companyTemplate/delete/{comDocumentId}
   */
  deleteTemplate(comDocumentId: string): Observable<TemplateApiResponse<{}>> {
    return this.apiService.delete(`${this.BASE_PATH}/delete/${comDocumentId}`);
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  /**
   * Check if API response is successful
   */
  isSuccess(response: TemplateApiResponse<any>): boolean {
    return response.code === '00000';
  }

  /**
   * Check if API response indicates no data found
   */
  isNoData(response: TemplateApiResponse<any>): boolean {
    return response.code === '22222';
  }

  /**
   * Check if API response indicates an error
   */
  isError(response: TemplateApiResponse<any>): boolean {
    return response.code === '11111';
  }
}
